import { useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import type {
  MatchEventFrame,
  MatchOperation,
} from '../types/matchEvents.types';
import { compareStreamIds } from '../utils/notificationsFeed';
import { MAX_MATCH_EVENT_ROWS } from '../utils/matchEventsFeed';
import { operationKey, boundSeenOps } from '../utils/matchOperationsFeed';

/**
 * Seconds until a JWT's `exp`, or null if it can't be decoded. Local
 * copy (AuthContext's helper is not exported) — only reads `exp`. Mirror
 * of the same helper in `useNotificationsSocket`.
 */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/** Refresh the token this many seconds BEFORE it expires. */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/** Cooldown between auto-refresh attempts on a connect/auth error. */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/** The WS namespace for the live match-events feed (frozen contract §WS). */
export const MATCH_EVENTS_NAMESPACE = '/match-events';

/**
 * Upper bound on the seen-operation-key set. Operations carry no streamId, so a
 * resubscribe re-delivery is de-duped by remembering keys — but an unbounded Set
 * grows for the whole session. We keep well more than the visible buffer
 * ({@link MAX_MATCH_EVENT_ROWS}) so redelivery within the retained window still
 * de-dupes, and evict the oldest keys once the cap is exceeded.
 */
export const MAX_SEEN_OPS = MAX_MATCH_EVENT_ROWS * 4;

/**
 * Resolve the namespaced socket URL. The feed shares the notifications-
 * websocket base (`VITE_NOTIFICATIONS_WS_URL`) and lives on the
 * `/match-events` socket.io namespace, so we append the namespace path
 * to the base URL (socket.io routes the trailing path to the namespace).
 * A dedicated `VITE_MATCH_EVENTS_WS_URL` overrides the base if the feed
 * is ever split onto its own host (mirrors the `VITE_LIVE_PREDICTIONS_WS_URL`
 * fallback precedent).
 */
export function resolveMatchEventsWsUrl(): string {
  const base =
    import.meta.env.VITE_MATCH_EVENTS_WS_URL ||
    import.meta.env.VITE_NOTIFICATIONS_WS_URL ||
    '';
  if (!base) return '';
  return `${base.replace(/\/+$/, '')}${MATCH_EVENTS_NAMESPACE}`;
}

export interface UseMatchEventsSocketOptions {
  /** The fixtures to subscribe to (the live hub's ACTIVE set). */
  fixtureIds: number[];
  /** Request the raw firehose (`player_stat` + `timer`) for every fixture. */
  includeRaw?: boolean;
}

export interface MatchEventsSocketApi {
  isConnected: boolean;
  /** Buffered incident frames across ALL subscribed fixtures (append order). */
  frames: MatchEventFrame[];
  /** Buffered `match_operation`s across ALL subscribed fixtures (append order). */
  operations: MatchOperation[];
}

/**
 * Connects to the notifications-websocket service on the `/match-events`
 * namespace and streams the live hub: `match_event` incident frames AND
 * `match_operation` sniper/manual actions for EVERY active fixture.
 *
 * Subscription model (frozen contract §WS `/match-events`):
 *  - The desired set = `fixtureIds`. On (re)connect we (re)subscribe the whole
 *    set (the server forgets our rooms across a reconnect); while connected we
 *    diff the set and `subscribe_match` newcomers / `unsubscribe_match` leavers.
 *  - Incidents arrive on `match_event`, operations on `match_operation`; each
 *    incident carries a per-fixture `streamId` cursor so a resubscribe replays
 *    via `XRANGE` with no gap. Operations carry no cursor and are de-duped by a
 *    composite {@link operationKey}.
 *
 * Auth / resilience: reuses the notifications-socket pattern VERBATIM —
 * socket.io owns an infinite, backed-off reconnect loop; we proactively
 * refresh the JWT before `exp`; on `connect_error`/`auth_error` we
 * refresh-and-retry (once per cooldown) without ever tearing the socket
 * down or logging the operator out (`clearOnFailure: false`).
 */
export function useMatchEventsSocket(
  options: UseMatchEventsSocketOptions,
): MatchEventsSocketApi {
  const { fixtureIds, includeRaw = false } = options;
  const wsUrl = resolveMatchEventsWsUrl();
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [frames, setFrames] = useState<MatchEventFrame[]>([]);
  const [operations, setOperations] = useState<MatchOperation[]>([]);

  // Per-fixture replay cursor — id of the most-recently-applied stream entry
  // for each fixture. Empty means "never applied one" so its first subscribe
  // omits the cursor.
  const cursorsRef = useRef<Map<number, string>>(new Map());
  // Seen operation keys — operations carry no streamId, so a resubscribe
  // re-delivery is de-duped by composite key.
  const seenOpsRef = useRef<Set<string>>(new Set());

  // The currently-subscribed fixtures — so a set change can leave the rooms
  // that dropped out before joining the new ones.
  const subscribedRef = useRef<Set<number>>(new Set());
  // Latest desired set + includeRaw, read by the (stable) subscribe closure.
  const desiredRef = useRef<number[]>(fixtureIds);
  desiredRef.current = fixtureIds;
  const includeRawRef = useRef<boolean>(includeRaw);
  includeRawRef.current = includeRaw;

  const lastAuthRecoveryAt = useRef(0);

  // A stable dependency for the reconcile effect (the array identity churns).
  const fixtureIdsKey = [...fixtureIds].sort((a, b) => a - b).join(',');

  // ── Frame + operation intake ────────────────────────────────────────────
  useEffect(() => {
    // Read the subscribed set fresh on each event — the reconcile effect
    // swaps the Set object on reconnect, so capturing it here would go stale.
    const onMatchEvent = (frame: MatchEventFrame) => {
      const subscribed = subscribedRef.current;
      // Ignore stray frames for a fixture we're no longer subscribed to.
      if (subscribed.size > 0 && !subscribed.has(frame.fixtureId)) return;
      const incomingId = frame.streamId;
      if (incomingId) {
        const cursor = cursorsRef.current.get(frame.fixtureId) ?? '';
        const cmp = cursor ? compareStreamIds(incomingId, cursor) : 1;
        // Already-applied stream id for this fixture — a replay re-delivery.
        if (cmp <= 0) return;
        cursorsRef.current.set(frame.fixtureId, incomingId);
      }
      setFrames((prev) => {
        const next = [...prev, frame];
        return next.length > MAX_MATCH_EVENT_ROWS
          ? next.slice(next.length - MAX_MATCH_EVENT_ROWS)
          : next;
      });
    };

    const onMatchOperation = (op: MatchOperation) => {
      const subscribed = subscribedRef.current;
      if (subscribed.size > 0 && !subscribed.has(op.fixtureId)) return;
      const key = operationKey(op);
      const seen = seenOpsRef.current;
      if (seen.has(key)) return;
      seen.add(key);
      // Bound the de-dupe set so a long session doesn't leak (evicts oldest).
      boundSeenOps(seen, MAX_SEEN_OPS);
      // Per-fixture op cap: not applied here — the shared MAX_MATCH_EVENT_ROWS
      // buffer is a flat append list, so a per-fixture quota would require
      // grouping on every intake. Left out intentionally (noisy-match starvation
      // is bounded by the row cap; revisit only if a real starvation shows up).
      setOperations((prev) => {
        const next = [...prev, op];
        return next.length > MAX_MATCH_EVENT_ROWS
          ? next.slice(next.length - MAX_MATCH_EVENT_ROWS)
          : next;
      });
    };

    socket.on('match_event', onMatchEvent);
    socket.on('match_operation', onMatchOperation);
    return () => {
      socket.off('match_event', onMatchEvent);
      socket.off('match_operation', onMatchOperation);
    };
  }, [socket]);

  // ── (Re)subscribe: reconcile the desired set with the joined rooms ──────
  useEffect(() => {
    const emitSubscribe = (fixtureId: number) => {
      const cursor = cursorsRef.current.get(fixtureId);
      socket.emit('subscribe_match', {
        fixtureId,
        ...(cursor ? { lastStreamId: cursor } : {}),
        includeRaw: includeRawRef.current,
      });
    };

    // Full (re)subscribe — the server forgot our rooms across the (re)connect.
    const resubscribeAll = () => {
      subscribedRef.current = new Set();
      reconcile();
    };

    // Diff the desired set against the joined rooms; join newcomers, leave
    // fixtures that dropped out of the active set.
    const reconcile = () => {
      const desired = new Set(desiredRef.current);
      const subscribed = subscribedRef.current;
      for (const fx of subscribed) {
        if (!desired.has(fx)) {
          socket.emit('unsubscribe_match', { fixtureId: fx });
          subscribed.delete(fx);
        }
      }
      for (const fx of desired) {
        if (!subscribed.has(fx)) {
          emitSubscribe(fx);
          subscribed.add(fx);
        }
      }
    };

    socket.on('connect', resubscribeAll);
    if (socket.connected) reconcile();

    return () => {
      socket.off('connect', resubscribeAll);
    };
  }, [socket, socket.connected, fixtureIdsKey]);

  // ── includeRaw toggle: force a FULL resubscribe of the active set ─────────
  // The reconcile effect keys on the fixture SET only (includeRaw is read from a
  // ref), so flipping "Raw / all incidents" while connected with a stable set
  // would be a silent no-op — the server would never start/stop the
  // player_stat+timer firehose. Re-emit subscribe_match for every joined fixture
  // with the new includeRaw. Skip the first run (the initial subscribe is owned
  // by the reconcile effect) to avoid a double subscribe on mount; while
  // disconnected we skip too — the connect handler resubscribes from the fresh
  // ref, so there's no gap.
  const includeRawInitialisedRef = useRef(false);
  useEffect(() => {
    if (!includeRawInitialisedRef.current) {
      includeRawInitialisedRef.current = true;
      return;
    }
    if (!socket.connected) return;
    for (const fx of subscribedRef.current) {
      const cursor = cursorsRef.current.get(fx);
      socket.emit('subscribe_match', {
        fixtureId: fx,
        ...(cursor ? { lastStreamId: cursor } : {}),
        includeRaw,
      });
    }
  }, [socket, includeRaw]);

  // ── Leave every joined room on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      for (const fx of subscribedRef.current) {
        try {
          socket.emit('unsubscribe_match', { fixtureId: fx });
        } catch {
          /* socket already torn down */
        }
      }
    };
  }, [socket]);

  // ── Proactive token refresh before `exp` ────────────────────────────────
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return;
    const delayMs = Math.max(0, (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000);
    const timer = setTimeout(() => {
      refreshAuthToken({ clearOnFailure: false }).catch((err) => {
        Sentry.captureException(err, { tags: { area: 'match-events-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── Auth-error recovery: refresh-and-retry, NEVER give up ───────────────
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = Date.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        Sentry.captureException(
          err instanceof Error ? err : new Error('match-events WS auth error'),
          { tags: { area: 'match-events-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        newToken = await refreshAuthToken({ clearOnFailure: false });
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'match-events-ws' },
        });
      }

      if (newToken) {
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        toast.error(
          'Live match events reconnecting — will recover automatically.',
          { toastId: 'match-events-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('match-events WS auth error — refresh failed'),
          { tags: { area: 'match-events-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  return {
    isConnected: socket.connected,
    frames,
    operations,
  };
}
